const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://admin.goridesrw.com/api/remote';
export const ACCOUNT_VERIFICATION_ROUTE = '/account-verification';
const PENDING_VERIFICATION_CUSTOMER_ID_KEY = 'pendingVerificationCustomerId';
const PENDING_VERIFICATION_ROLE_KEY = 'pendingVerificationRole';
const PENDING_VERIFICATION_PURPOSE_KEY = 'pendingVerificationPurpose';

export const getDefaultRouteForRole = (role) => {
  switch (role) {
    case 'DRIVER':
      return '/driver-dashboard';
    case 'CAR_OWNER':
      return '/owner-dashboard';
    case 'RENTER':
      return '/renter-dashboard';
    default:
      return '/';
  }
};

export const isVerificationComplete = () => localStorage.getItem('verificationComplete') === 'true';

export const getPostAuthRoute = (user) => {
  if (!user?.verificationComplete) {
    return ACCOUNT_VERIFICATION_ROUTE;
  }

  return getDefaultRouteForRole(user.customerType || localStorage.getItem('userRole'));
};

export const setUserProfile = (user) => {
  if (!user) {
    clearTokens();
    return;
  }

  localStorage.setItem('firstName', user.firstName ?? localStorage.getItem('firstName') ?? '');
  localStorage.setItem('lastName', user.lastName ?? localStorage.getItem('lastName') ?? '');
  localStorage.setItem('email', user.email ?? localStorage.getItem('email') ?? '');
  localStorage.setItem('phone', user.phone ?? localStorage.getItem('phone') ?? '');
  if (user.capabilities) localStorage.setItem('userCapabilities', JSON.stringify(user.capabilities));
  localStorage.setItem('userRole', user.customerType ?? localStorage.getItem('userRole') ?? '');
  localStorage.setItem('identificationType', user.identificationType ?? localStorage.getItem('identificationType') ?? '');
  localStorage.setItem('identificationNumber', user.identificationNumber ?? localStorage.getItem('identificationNumber') ?? '');
  localStorage.setItem('identificationProofUploaded', user.identificationProofUploaded ? 'true' : 'false');
  localStorage.setItem('partnershipContractUploaded', user.partnershipContractUploaded ? 'true' : 'false');
  localStorage.setItem('verificationComplete', user.verificationComplete ? 'true' : 'false');
  localStorage.setItem('requiresVerification', user.requiresVerification ? 'true' : 'false');
  window.dispatchEvent(new Event('auth-profile-updated'));
};

export const setPendingVerificationContext = ({ customerId, role, purpose }) => {
  if (customerId) {
    localStorage.setItem(PENDING_VERIFICATION_CUSTOMER_ID_KEY, customerId);
  }
  if (role) {
    localStorage.setItem(PENDING_VERIFICATION_ROLE_KEY, role);
  }
  if (purpose) {
    localStorage.setItem(PENDING_VERIFICATION_PURPOSE_KEY, purpose);
  }
};

export const getPendingVerificationContext = () => ({
  customerId: localStorage.getItem(PENDING_VERIFICATION_CUSTOMER_ID_KEY),
  role: localStorage.getItem(PENDING_VERIFICATION_ROLE_KEY),
  purpose: localStorage.getItem(PENDING_VERIFICATION_PURPOSE_KEY),
});

export const clearPendingVerificationContext = () => {
  localStorage.removeItem(PENDING_VERIFICATION_CUSTOMER_ID_KEY);
  localStorage.removeItem(PENDING_VERIFICATION_ROLE_KEY);
  localStorage.removeItem(PENDING_VERIFICATION_PURPOSE_KEY);
};

// Store JWT and Refresh Tokens
export const setTokens = (token, refreshToken) => {
  if (token) localStorage.setItem('token', token);
  if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
  if (token) {
    clearPendingVerificationContext();
  }
};

export const getTokens = () => {
  return {
    token: localStorage.getItem('token'),
    refreshToken: localStorage.getItem('refreshToken')
  };
};

export const clearTokens = () => {
  clearPendingVerificationContext();
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userRole');
  localStorage.removeItem('userCapabilities');
  localStorage.removeItem('firstName');
  localStorage.removeItem('lastName');
  localStorage.removeItem('email');
  localStorage.removeItem('phone');
  localStorage.removeItem('identificationType');
  localStorage.removeItem('identificationNumber');
  localStorage.removeItem('identificationProofUploaded');
  localStorage.removeItem('partnershipContractUploaded');
  localStorage.removeItem('verificationComplete');
  localStorage.removeItem('requiresVerification');
};

const redirectToLogin = () => {
  clearTokens();
  if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
    window.location.assign('/login');
  }
};

const redirectToVerification = () => {
  localStorage.setItem('verificationComplete', 'false');
  localStorage.setItem('requiresVerification', 'true');

  if (typeof window !== 'undefined' && window.location.pathname !== ACCOUNT_VERIFICATION_ROUTE) {
    window.location.assign(ACCOUNT_VERIFICATION_ROUTE);
  }
};

const isExpiredTokenResponse = async (response) => {
  if (!response) {
    return false;
  }

  if (response.status === 401) {
    return true;
  }

  try {
    const data = await response.clone().json();
    const message = String(data?.message || '').toLowerCase();
    return message.includes('jwt token has expired') || message.includes('invalid or expired refresh token');
  } catch (error) {
    return false;
  }
};

const isVerificationRequiredResponse = async (response) => {
  if (!response || response.status !== 403) {
    return false;
  }

  try {
    const data = await response.clone().json();
    return Boolean(data?.requiresVerification);
  } catch (error) {
    return false;
  }
};

let refreshPromise = null;

export const refreshAccessToken = async () => {
  const { refreshToken } = getTokens();
  if (!refreshToken) {
    redirectToLogin();
    throw new Error('Session expired');
  }

  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(handleResponse)
      .then((data) => {
        setTokens(data.token, data.refreshToken);
        setUserProfile(data);
        return data.token;
      })
      .catch((error) => {
        redirectToLogin();
        throw error;
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
};

export const authenticatedFetch = async (url, options = {}, hasRetried = false) => {
  const headers = {
    ...(options.headers || {}),
  };

  const token = localStorage.getItem('token');
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (await isVerificationRequiredResponse(response)) {
    redirectToVerification();
    return response;
  }

  if (!hasRetried && await isExpiredTokenResponse(response)) {
    const newToken = await refreshAccessToken();
    return authenticatedFetch(url, {
      ...options,
      headers: {
        ...(options.headers || {}),
        Authorization: `Bearer ${newToken}`,
      },
    }, true);
  }

  if (response.status === 401 && hasRetried) {
    redirectToLogin();
  }

  return response;
};

const handleResponse = async (response) => {
  const data = await response.json();
  if (!response.ok || data.action === 0) {
    const error = new Error(data.message || 'An error occurred');
    error.details = data;
    throw error;
  }
  return data;
};

const handleAuthFlowResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.message || 'An error occurred');
    error.details = data;
    throw error;
  }
  return data;
};

const handleJsonResponse = async (response) => {
  const data = await response.json();
  if (!response.ok) {
    const error = new Error(data.message || 'An error occurred');
    error.details = data;
    throw error;
  }
  return data;
};

export const authService = {
  // Login
  login: async (email, password) => {
    const response = await fetch(`${BASE_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    return handleAuthFlowResponse(response);
  },

  googleLogin: async (credential, customerType) => {
    const response = await fetch(`${BASE_URL}/auth/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        credential,
        ...(customerType ? { customerType } : {}),
      }),
    });
    return handleAuthFlowResponse(response);
  },

  // Signup
  signup: async (userData) => {
    // Expected structure: email, password, firstName, lastName, phone, role/customerType...
    const response = await fetch(`${BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });
    return handleResponse(response);
  },

  // Verify OTP
  verifyOtp: async (customerId, otp) => {
    const response = await fetch(`${BASE_URL}/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, otp: parseInt(otp, 10) }),
    });
    return handleResponse(response);
  },

  verifyLoginOtp: async (customerId, otp) => {
    const response = await fetch(`${BASE_URL}/auth/verify-login-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ customerId, otp: parseInt(otp, 10) }),
    });
    return handleResponse(response);
  },

  // Resend OTP
  resendOtp: async (customerId) => {
    const response = await fetch(`${BASE_URL}/auth/resend-otp?customerId=${customerId}`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  resendLoginOtp: async (customerId) => {
    const response = await fetch(`${BASE_URL}/auth/resend-login-otp?customerId=${customerId}`, {
      method: 'POST',
    });
    return handleResponse(response);
  },

  getVerificationStatus: async () => {
    const response = await authenticatedFetch(`${BASE_URL}/customer-verification/status`);
    return handleJsonResponse(response);
  },

  updateVerificationDetails: async (identificationType, identificationNumber, phone) => {
    const response = await authenticatedFetch(`${BASE_URL}/customer-verification/details`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identificationType, identificationNumber, phone }),
    });
    return handleJsonResponse(response);
  },

  uploadIdentificationProof: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await authenticatedFetch(`${BASE_URL}/customer-verification/identification-proof`, {
      method: 'POST',
      body: formData,
    });
    return handleJsonResponse(response);
  },

  uploadPartnershipContract: async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await authenticatedFetch(`${BASE_URL}/customer-verification/partnership-contract`, {
      method: 'POST',
      body: formData,
    });
    return handleJsonResponse(response);
  },

  // Logout
  logout: async () => {
    const { token } = getTokens();
    if (!token) {
      clearTokens();
      return;
    }
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ token }),
      });
    } finally {
      clearTokens();
    }
  }
};

export const hasCapability = (role) => {
  if (role === 'RENTER') return Boolean(localStorage.getItem('token'));
  try { return JSON.parse(localStorage.getItem('userCapabilities') || '[]').includes(role) || localStorage.getItem('userRole') === role; }
  catch { return localStorage.getItem('userRole') === role; }
};
